import { memo, useCallback, useEffect } from 'react';
import { sessionService } from 'redux-react-session';
import { useNavigate, useLocation, useParams, useSearchParams, Navigate } from 'react-router-dom';
import _includes from 'lodash/includes';
// Local files
import axios from 'apis';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useProfile from 'hooks/useProfile';
import useSessions from 'hooks/useSessions';
import useNylas from 'hooks/useNylas';
import useStore from 'hooks/useStore';

const Session = () => {
  const navigate = useNavigate();
  const { checked, authenticated } = useCustomSelector(state => state.session);
  const { getProfile } = useProfile();
  const { clearStore } = useStore();
  const { createNylas } = useNylas();
  const { setLocalSession } = useSessions();
  const { invitation_token, admin_access_token, token } = useParams();
  const { pathname } = useLocation();
  const godModeAccess = _includes(pathname, 'admin_access')
  const [searchParams] = useSearchParams();
 
  const processProfile = useCallback(session => {
    const controller = new AbortController();

    axios.defaults.signal = controller.signal;
    axios.defaults.params = { ...axios.defaults.params, access_token: session.accessToken };
    axios.defaults.data = { ...axios.defaults.params, access_token: session.accessToken };

    axios.interceptors.request.use(config => config, error => {
      console.error(error);
      return Promise.reject(error);
    });
    axios.interceptors.response.use(response => response, error => {
      return Promise.reject(error);
    });
    setLocalSession(session);
  }, [ setLocalSession]);


  useEffect(() => {
    const checkInitialSession = async () => {
      const session = await sessionService.loadSession().catch(e => console.warn(e));
      if (session) {
        axios.defaults.params = { ...axios.defaults.params, access_token: session.accessToken };
        axios.defaults.data = { ...axios.defaults.params, access_token: session.accessToken };
  
        getProfile()
        .then(({ error }) => {
          if (!error) {
            processProfile(session);
          }
        });
      } else {
        setLocalSession({ accessToken: null });
      }
    };

    if (checked && !godModeAccess) checkInitialSession();

  }, [checked, getProfile, processProfile, setLocalSession, godModeAccess]); 

  useEffect(() => {
    const code = searchParams.get('code');
    const templateId = searchParams.get('templateId');

    if (pathname === '/nylas_callback') {
      createNylas(code)
      .then(({ payload, error }) => {
        if (!error) {
          if (payload.status === 204) {
            getProfile()
            .then(({ error }) => {
              if (!error) navigate('/settings')
            })
          } 
        } 
      })
    }
    if ((godModeAccess && admin_access_token) || (token && templateId)) {
      const checkInitialSession = async () => {
        const session = await sessionService.loadSession().catch(e => console.warn(e));
        if (session) {
          await sessionService.deleteSession();
          clearStore();
          axios.defaults.signal = undefined;
          axios.defaults.params = undefined;
          axios.defaults.data = undefined;
        } else {
          setLocalSession({ accessToken: null });
        }
      }
      checkInitialSession()
      .then(() => {
        sessionService.saveSession({ accessToken: admin_access_token || token })
        .then(() =>  navigate(admin_access_token ? '/' : `/templates/${templateId}/builder`))
      })    
    }
  }, [searchParams, pathname, createNylas, getProfile]); //eslint-disable-line

  if (checked && !authenticated && !!!invitation_token && !!!token) return <Navigate to='/sign_in' replace />;
  if (checked && !authenticated && !!invitation_token) return <Navigate to='/sign_up' state={{ invitation_token }} replace />;
  if (pathname === '/' && checked && authenticated) return <Navigate to='/dashboard' replace />;

  return null;
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <Session {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);